import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { withMedicalHistoryRecordFixture } from "@src/packages/medical-history/test-fixtures/medicalHistoryRecordFixture";
import type { SurgeryType } from "./options";

export interface SurgeryDetail {
  id?: string;
  surgery: SurgeryType;
  other?: string | null;
  date?: string | null;
  comments?: string | null;
}

export interface SurgeriesState {
  hasSurgeries: boolean | null;
  surgeries: SurgeryDetail[];
  surgeryIdsToDelete: string[];
}

interface SurgeriesContextType {
  state: SurgeriesState;
  updateState: (updates: Partial<SurgeriesState>) => void;
  resetState: () => void;
}

const SurgeriesContext = createContext<SurgeriesContextType | undefined>(
  undefined,
);

const initialState: SurgeriesState = {
  hasSurgeries: null,
  surgeries: [],
  surgeryIdsToDelete: [],
};

export function SurgeriesProvider({ children }: { children: React.ReactNode }) {
  const { data: rawPatient, isLoading } = usePatient();
  const patient = withMedicalHistoryRecordFixture(rawPatient);
  const [state, setState] = useState<SurgeriesState>(initialState);

  useEffect(() => {
    if (isLoading || !patient) return;

    const medicalHistory = patient.medicalHistory;
    if (!medicalHistory) return;

    setState((prev) => {
      const isInitialState =
        prev.hasSurgeries === null && prev.surgeries.length === 0;
      if (!isInitialState) return prev;

      const next: Partial<SurgeriesState> = {};

      if (
        medicalHistory.hasSurgeries !== undefined &&
        medicalHistory.hasSurgeries !== null
      ) {
        next.hasSurgeries = medicalHistory.hasSurgeries;
      }

      if (
        Array.isArray(medicalHistory.surgeries) &&
        medicalHistory.surgeries.length > 0
      ) {
        next.hasSurgeries = true;
        next.surgeries = medicalHistory.surgeries.map((s) => ({
          id: s.id,
          surgery: s.surgery ?? "other",
          other: s.other ?? null,
          date: s.date ?? null,
          comments: s.comments ?? null,
        }));
      }

      if (Object.keys(next).length === 0) return prev;

      return { ...prev, ...next };
    });
  }, [patient, isLoading]);

  const updateState = useCallback((updates: Partial<SurgeriesState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const resetState = useCallback(() => {
    setState(initialState);
  }, []);

  return (
    <SurgeriesContext.Provider value={{ state, updateState, resetState }}>
      {children}
    </SurgeriesContext.Provider>
  );
}

export function useSurgeries() {
  const context = useContext(SurgeriesContext);
  if (context === undefined) {
    throw new Error("useSurgeries must be used within a SurgeriesProvider");
  }
  return context;
}
